import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { departmentsApi, reorderApi } from "../api";
import type { Department } from "../types";
import { GripVertical, Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import DepartmentForm from "./components/DepartmentForm";

export default function DepartmentsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-departments"], queryFn: departmentsApi.list });
  const [list, setList] = useState<Department[]>([]);
  const [editing, setEditing] = useState<Department | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (data?.departments) setList(data.departments);
  }, [data]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderApi.apply({ type: "department", ordered_ids: ids }),
    onSuccess: () => {
      toast.success("Order saved");
      qc.invalidateQueries({ queryKey: ["admin-departments"] });
      qc.invalidateQueries({ queryKey: ["content"] });
    },
    onError: () => toast.error("Reorder failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => departmentsApi.remove(id),
    onSuccess: () => {
      toast.success("Department deleted");
      qc.invalidateQueries({ queryKey: ["admin-departments"] });
      qc.invalidateQueries({ queryKey: ["content"] });
    },
  });

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = list.findIndex((d) => d.id === active.id);
    const newIdx = list.findIndex((d) => d.id === over.id);
    const next = arrayMove(list, oldIdx, newIdx);
    setList(next);
    reorder.mutate(next.map((d) => d.id));
  }

  return (
    <div className="max-w-5xl mx-auto px-10 py-10">
      <header className="mb-7 flex items-end justify-between">
        <div>
          <h1 className="display text-[36px] font-medium tracking-tight">
            <span className="italic">Departments</span>
          </h1>
          <p className="text-[13.5px] text-ink-500 mt-1.5">
            Drag to reorder. Click a department to manage its sections and services.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <Plus className="size-3.5" /> New department
        </button>
      </header>

      <div className="surface overflow-hidden">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={list.map((d) => d.id)} strategy={verticalListSortingStrategy}>
            <ul className="divide-y divide-ink-100">
              {list.map((d) => (
                <DepartmentRow
                  key={d.id}
                  dept={d}
                  onEdit={() => setEditing(d)}
                  onDelete={() => {
                    if (confirm(`Delete "${d.name}"? This removes all its sections and tiles.`)) {
                      remove.mutate(d.id);
                    }
                  }}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
        {list.length === 0 && (
          <div className="px-8 py-12 text-center text-[13px] text-ink-500">
            No departments yet. Create the first one.
          </div>
        )}
      </div>

      {(creating || editing) && (
        <DepartmentForm
          existing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-departments"] });
            qc.invalidateQueries({ queryKey: ["content"] });
          }}
        />
      )}
    </div>
  );
}

function DepartmentRow({
  dept,
  onEdit,
  onDelete,
}: {
  dept: Department;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dept.id,
  });
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="px-3.5 py-3 flex items-center gap-3 hover:bg-cream-50 group"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-ink-300 hover:text-ink-700 transition-colors"
        aria-label="drag"
      >
        <GripVertical className="size-3.5" />
      </button>
      <span className="dot shrink-0" style={{ backgroundColor: dept.color }} aria-hidden />
      <div className="flex-1 min-w-0">
        <Link
          to={`/admin/departments/${dept.id}`}
          className="display text-[15px] font-medium text-ink-900 hover:underline tracking-tight"
        >
          {dept.name}
        </Link>
        {dept.description && (
          <span className="text-[12px] text-ink-500 ml-2 truncate">· {dept.description}</span>
        )}
      </div>
      <span className="text-[11px] text-ink-400 font-mono">/{dept.slug}</span>
      {dept.hidden && (
        <span className="chip">
          <EyeOff className="size-3" /> Hidden
        </span>
      )}
      <button onClick={onEdit} className="btn-icon" aria-label="edit">
        <Pencil className="size-3.5" />
      </button>
      <button
        onClick={onDelete}
        className="btn-icon hover:!bg-accent-50 hover:!text-accent-600"
        aria-label="delete"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}
